import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateDemandExamplePayload,
  DemandExample,
  DemandExampleFilters,
  DemandExampleListResponse,
  UpdateDemandExamplePayload,
} from '../types/demand-example-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const demandExampleApiClient = createApi({
  reducerPath: 'demandExampleApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['DemandExamples'] as const,
  endpoints: (builder) => ({
    // List demand examples with filtering and pagination
    listDemandExamples: builder.query<
      DemandExampleListResponse,
      Partial<DemandExampleFilters>
    >({
      query: (params = {}) => ({
        url: 'api/demands/examples/',
        params: {
          ...params,
          // Booleans must be sent as 'true'/'false' strings for query params
          has_templates:
            typeof params.has_templates === 'boolean'
              ? String(params.has_templates)
              : params.has_templates,
          // Convert array filters to comma-separated strings if needed
          jurisdiction: Array.isArray(params.jurisdiction)
            ? params.jurisdiction.join(',')
            : params.jurisdiction,
          practice_areas: Array.isArray(params.practice_areas)
            ? params.practice_areas.join(',')
            : params.practice_areas,
          tags: Array.isArray(params.tags)
            ? params.tags.join(',')
            : params.tags,
        },
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.results.map(({ id }) => ({
              type: 'DemandExamples' as const,
              id,
            })),
            { type: 'DemandExamples', id: 'LIST' },
          ];
        }
        return [{ type: 'DemandExamples', id: 'LIST' }];
      },
    }),

    // Get a single demand example by ID
    getDemandExample: builder.query<DemandExample, string>({
      query: (id) => `api/demands/examples/${id}/`,

      providesTags: (result, error, id) => [{ type: 'DemandExamples', id }],
    }),

    // Create a new demand example
    createDemandExample: builder.mutation<
      DemandExample,
      CreateDemandExamplePayload
    >({
      query: (data) => {
        // Check if we have a file upload
        if (data.demand) {
          const formData = new FormData();

          // Add all text fields to FormData
          if (data.firm) formData.append('firm', data.firm);
          if (data.matter_type)
            formData.append('matter_type', data.matter_type);
          if (data.title) formData.append('title', data.title);
          if (data.description)
            formData.append('description', data.description);
          if (data.transform_id)
            formData.append('transform_id', data.transform_id);

          // Add array fields as multiple entries
          if (data.jurisdiction) {
            data.jurisdiction.forEach((item) =>
              formData.append('jurisdiction', item),
            );
          }
          if (data.practice_areas) {
            data.practice_areas.forEach((item) =>
              formData.append('practice_areas', item),
            );
          }
          if (data.tags) {
            data.tags.forEach((item) => formData.append('tags', item));
          }

          // Add the file
          formData.append('demand', data.demand);

          return {
            url: 'api/demands/examples/',
            method: 'POST',
            body: formData,
          };
        }

        // No file upload, send as JSON
        return {
          url: 'api/demands/examples/',
          method: 'POST',
          body: data,
        };
      },
      invalidatesTags: [{ type: 'DemandExamples', id: 'LIST' }],
    }),

    // Update a demand example
    updateDemandExample: builder.mutation<
      DemandExample,
      { id: string; data: UpdateDemandExamplePayload }
    >({
      query: ({ id, data }) => {
        // Updates only send JSON data - no file uploads for edits
        return {
          url: `api/demands/examples/${id}/`,
          method: 'PATCH',
          body: data,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'DemandExamples', id },
        { type: 'DemandExamples', id: 'LIST' },
      ],
    }),

    // Delete a demand example
    deleteDemandExample: builder.mutation<void, string>({
      query: (id) => ({
        url: `api/demands/examples/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'DemandExamples', id },
        { type: 'DemandExamples', id: 'LIST' },
      ],
    }),

    triggerDemandSectionsAnalysis: builder.mutation<
      { message: string; workflow_id: string; status: string },
      { demandExampleId: string }
    >({
      query: ({ demandExampleId }) => ({
        url: `api/demands/examples/${demandExampleId}/analyze-sections/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, { demandExampleId }) => [
        { type: 'DemandExamples', id: demandExampleId },
      ],
    }),

    // New query for polling the analysis status and data
    getDemandSectionsAnalysis: builder.query<
      {
        status: DemandExample['sections_analysis_status'];
        data: DemandExample['sections_analysis_data'];
      },
      string // demandExampleId
    >({
      query: (demandExampleId) =>
        `api/demands/examples/${demandExampleId}/analyze-sections/`,
      // This provides a unique tag for this specific analysis polling,
      // different from the main DemandExample object if needed, or can be the same.
      // For simplicity, let's use a specific tag or reuse the main one if appropriate for your caching strategy.
      providesTags: (result, error, id) => [
        { type: 'DemandExamples', id: `${id}-analysis` },
      ],
    }),
  }),
});

export const {
  useListDemandExamplesQuery,
  useGetDemandExampleQuery,
  useCreateDemandExampleMutation,
  useUpdateDemandExampleMutation,
  useDeleteDemandExampleMutation,
  useTriggerDemandSectionsAnalysisMutation,
  useGetDemandSectionsAnalysisQuery,
} = demandExampleApiClient;
